
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate, Link } from 'react-router-dom';
import { Terminal, RefreshCw, Trash2, Shield, Clock, Calculator, Search, ChevronRight, CheckCircle, AlertTriangle, Zap, Gavel, Info, PlusCircle, Car, User, Megaphone, Send, Save, Users, UserCheck, UserMinus, UserX, CalendarDays, Loader2, Radio } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { apiRequest } from '../utils/api';

// --- TYPES FOR AUDIT ---
interface AuditDriver {
    id: string;
    username: string;
    team_color: string;
    total_points: number;
    races_driven: number;
}

interface AuditTeam {
    id: string;
    name: string;
    color: string;
    total_points: number;
}

interface AuditDetailDriver {
    event_id: string;
    event_name: string;
    round: number;
    total_points: number;
    effective_points: number;
    is_test: boolean;
    position: number;
    status: string;
    sprint_pos: number | string;
    raw_points: number;
    raw_sprint: number;
    penalties: { id: number; points: number; description: string }[];
    components: {
        race: number;
        sprint: number;
        fl: number;
        clean: number;
        dotd: number;
        steward: number;
    };
    match: boolean;
}

interface AuditDetailTeam {
    event_name: string;
    round: number;
    total_points: number;
    effective_points: number; // New field
    is_test: boolean; // New field
    drivers: string;
    components: {
        raw_sum: number;
        clean_deduct: number;
        penalties: number;
    };
}

// --- AUDIT COMPONENT ---
const PointsAudit: React.FC = () => {
    const [view, setView] = useState<'DRIVERS' | 'TEAMS'>('DRIVERS');
    
    const [drivers, setDrivers] = useState<AuditDriver[]>([]);
    const [teams, setTeams] = useState<AuditTeam[]>([]);
    
    const [selectedDriver, setSelectedDriver] = useState<AuditDriver | null>(null);
    const [selectedTeam, setSelectedTeam] = useState<AuditTeam | null>(null);
    
    const [driverDetails, setDriverDetails] = useState<AuditDetailDriver[]>([]);
    const [teamDetails, setTeamDetails] = useState<AuditDetailTeam[]>([]);
    
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingField, setEditingField] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (view === 'DRIVERS') loadDrivers();
        else loadTeams();
    }, [view]);

    const loadDrivers = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<AuditDriver[]>('audit_points.php?mode=list');
            setDrivers(Array.isArray(res) ? res : []);
            setSelectedTeam(null);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };
    
    const loadTeams = async () => {
        setLoading(true);
        try {
            const res = await apiRequest<AuditTeam[]>('audit_points.php?mode=list_teams');
            setTeams(Array.isArray(res) ? res : []);
            setSelectedDriver(null);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const loadDriverDetails = async (driver: AuditDriver) => {
        setSelectedDriver(driver);
        setLoading(true);
        try {
            const res = await apiRequest<AuditDetailDriver[]>(`audit_points.php?mode=details&userId=${driver.id}`);
            setDriverDetails(Array.isArray(res) ? res : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const loadTeamDetails = async (team: AuditTeam) => {
        setSelectedTeam(team);
        setLoading(true);
        try {
            const res = await apiRequest<AuditDetailTeam[]>(`audit_points.php?mode=details_team&teamId=${team.id}`);
            setTeamDetails(Array.isArray(res) ? res : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleEditResult = async (userId: string, eventId: string, field: string, value: number) => {
        setSaving(true);
        try {
            await apiRequest('audit_points.php', 'POST', { action: 'edit_result', userId, eventId, field, value });
            if (selectedDriver) await loadDriverDetails(selectedDriver);
            await loadDrivers();
        } catch (e) { alert('Fehler beim Speichern: ' + e); }
        finally { setSaving(false); setEditingField(null); }
    };

    const handleEditPenalty = async (penaltyId: number, points: number) => {
        setSaving(true);
        try {
            await apiRequest('audit_points.php', 'POST', { action: 'edit_penalty', penaltyId, points });
            if (selectedDriver) await loadDriverDetails(selectedDriver);
            await loadDrivers();
        } catch (e) { alert('Fehler: ' + e); }
        finally { setSaving(false); setEditingField(null); }
    };

    const handleDeletePenalty = async (penaltyId: number, desc: string) => {
        if (!confirm(`"${desc}" wirklich löschen?`)) return;
        setSaving(true);
        try {
            await apiRequest('audit_points.php', 'POST', { action: 'delete_penalty', penaltyId });
            if (selectedDriver) await loadDriverDetails(selectedDriver);
            await loadDrivers();
        } catch (e) { alert('Fehler: ' + e); }
        finally { setSaving(false); }
    };

    const startEdit = (key: string, currentValue: number) => {
        setEditingField(key);
        setEditValue(String(currentValue));
    };

    const filteredDrivers = drivers.filter(d => d.username.toLowerCase().includes(search.toLowerCase()));
    const filteredTeams = teams.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-4 bg-gray-900 p-2 rounded">
                 <div className="flex gap-2">
                     <button onClick={() => setView('DRIVERS')} className={`px-4 py-2 rounded text-sm font-bold uppercase flex items-center gap-2 ${view==='DRIVERS'?'bg-blue-600 text-white':'text-gray-400 hover:text-white'}`}>
                         <User size={16}/> Fahrer
                     </button>
                     <button onClick={() => setView('TEAMS')} className={`px-4 py-2 rounded text-sm font-bold uppercase flex items-center gap-2 ${view==='TEAMS'?'bg-blue-600 text-white':'text-gray-400 hover:text-white'}`}>
                         <Car size={16}/> Teams
                     </button>
                 </div>
                 <div className="text-xs text-gray-500 italic mr-2">Testrennen zählen nicht zur Summe</div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
                {/* LEFT: List */}
                <div className="bg-[#1a1a20] border border-gray-700 rounded-lg flex flex-col overflow-hidden">
                    <div className="p-3 bg-gray-900 border-b border-gray-700">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input 
                                className="w-full bg-gray-800 border border-gray-600 rounded py-2 pl-9 text-xs text-white focus:border-f1-red outline-none"
                                placeholder={view === 'DRIVERS' ? "Fahrer suchen..." : "Team suchen..."}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                        {view === 'DRIVERS' ? filteredDrivers.map(d => (
                            <div 
                                key={d.id} 
                                onClick={() => loadDriverDetails(d)}
                                className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${selectedDriver?.id === d.id ? 'bg-blue-900/20 border border-blue-600' : 'bg-transparent border border-transparent hover:bg-gray-800'}`}
                            >
                                <div className="w-1 h-8 rounded-full" style={{backgroundColor: d.team_color || '#666'}}></div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-white truncate">{d.username}</div>
                                    <div className="text-[10px] text-gray-500">{d.races_driven} Rennen (gewertet)</div>
                                </div>
                                <div className="font-mono font-bold text-gray-300">{d.total_points}</div>
                                <ChevronRight size={14} className="text-gray-600"/>
                            </div>
                        )) : filteredTeams.map(t => (
                            <div 
                                key={t.id} 
                                onClick={() => loadTeamDetails(t)}
                                className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${selectedTeam?.id === t.id ? 'bg-blue-900/20 border border-blue-600' : 'bg-transparent border border-transparent hover:bg-gray-800'}`}
                            >
                                <div className="w-1 h-8 rounded-full" style={{backgroundColor: t.color || '#666'}}></div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-white truncate">{t.name}</div>
                                </div>
                                <div className="font-mono font-bold text-gray-300">{t.total_points}</div>
                                <ChevronRight size={14} className="text-gray-600"/>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT: Details */}
                <div className="lg:col-span-2 bg-[#1a1a20] border border-gray-700 rounded-lg flex flex-col overflow-hidden">
                    {view === 'DRIVERS' && selectedDriver ? (
                        <>
                            <div className="p-4 bg-gray-900 border-b border-gray-700 flex justify-between items-center">
                                <div>
                                    <h3 className="text-lg font-bold text-white uppercase italic">{selectedDriver.username}</h3>
                                    <div className="text-xs text-gray-400">Punkte Historie (Fahrer-WM)</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-mono font-bold text-white">{selectedDriver.total_points}</div>
                                    <div className="text-[10px] text-gray-500 uppercase font-bold">Total Points</div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                {driverDetails.map((row, idx) => (
                                    <div key={idx} className={`bg-gray-800/50 border border-gray-700 rounded p-3 ${row.is_test ? 'opacity-60 grayscale' : ''}`}>
                                        <div className="flex justify-between items-center mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold bg-gray-700 px-2 py-0.5 rounded text-gray-300">{row.round > 0 ? `R${row.round}` : 'TEST'}</span>
                                                <span className="font-bold text-white text-sm">{row.event_name}</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                 <div className={`font-mono font-bold text-lg ${row.is_test ? 'text-gray-500 line-through' : 'text-white'}`}>{row.total_points}</div>
                                                 {row.is_test && <span className="text-[9px] uppercase text-yellow-500 font-bold">Zählt nicht</span>}
                                            </div>
                                        </div>
                                        
                                        {/* Editable Points Grid */}
                                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-[10px] bg-gray-900/50 p-2 rounded border border-gray-800">
                                            {/* Race Points - Editable */}
                                            <div className="flex flex-col items-center border-r border-gray-700 cursor-pointer group/edit"
                                                onClick={() => startEdit(`pts-${idx}`, row.raw_points)}>
                                                <span className="text-gray-500 uppercase font-bold text-[8px]">Race</span>
                                                {editingField === `pts-${idx}` ? (
                                                    <input type="number" className="w-12 bg-gray-700 text-white text-center text-xs rounded px-1 py-0.5" autoFocus
                                                        value={editValue} onChange={e => setEditValue(e.target.value)}
                                                        onBlur={() => handleEditResult(selectedDriver!.id, row.event_id, 'points', Number(editValue))}
                                                        onKeyDown={e => { if (e.key === 'Enter') handleEditResult(selectedDriver!.id, row.event_id, 'points', Number(editValue)); if (e.key === 'Escape') setEditingField(null); }}
                                                    />
                                                ) : (
                                                    <span className="text-white group-hover/edit:text-f1-red transition-colors">+{row.components.race}</span>
                                                )}
                                            </div>
                                            {/* Sprint Points - Editable */}
                                            <div className="flex flex-col items-center border-r border-gray-700 cursor-pointer group/edit"
                                                onClick={() => startEdit(`spr-${idx}`, row.raw_sprint)}>
                                                <span className="text-gray-500 uppercase font-bold text-[8px]">Sprint</span>
                                                {editingField === `spr-${idx}` ? (
                                                    <input type="number" className="w-12 bg-gray-700 text-white text-center text-xs rounded px-1 py-0.5" autoFocus
                                                        value={editValue} onChange={e => setEditValue(e.target.value)}
                                                        onBlur={() => handleEditResult(selectedDriver!.id, row.event_id, 'sprint_points', Number(editValue))}
                                                        onKeyDown={e => { if (e.key === 'Enter') handleEditResult(selectedDriver!.id, row.event_id, 'sprint_points', Number(editValue)); if (e.key === 'Escape') setEditingField(null); }}
                                                    />
                                                ) : (
                                                    <span className="text-gray-300 group-hover/edit:text-f1-red transition-colors">+{row.components.sprint}</span>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-center border-r border-gray-700">
                                                <span className="text-gray-500 uppercase font-bold text-[8px]">FL</span>
                                                <span className={row.components.fl > 0 ? 'text-purple-400 font-bold' : 'text-gray-600'}>+{row.components.fl}</span>
                                            </div>
                                            <div className="flex flex-col items-center border-r border-gray-700">
                                                <span className="text-gray-500 uppercase font-bold text-[8px]">Clean</span>
                                                <span className={row.components.clean > 0 ? 'text-blue-400 font-bold' : 'text-gray-600'}>+{row.components.clean}</span>
                                            </div>
                                            <div className="flex flex-col items-center border-r border-gray-700">
                                                <span className="text-yellow-500 uppercase font-bold text-[8px]">DotD</span>
                                                <span className={row.components.dotd > 0 ? 'text-yellow-400 font-bold' : 'text-gray-600'}>+{row.components.dotd}</span>
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="text-red-500 uppercase font-bold text-[8px]">Stew.</span>
                                                <span className={row.components.steward < 0 ? 'text-red-400 font-bold' : row.components.steward > 0 ? 'text-green-400 font-bold' : 'text-gray-600'}>{row.components.steward > 0 ? '+' : ''}{row.components.steward}</span>
                                            </div>
                                        </div>

                                        {/* Penalty Entries - Editable */}
                                        {row.penalties && row.penalties.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                {row.penalties.map((pen: any) => (
                                                    <div key={pen.id} className="flex items-center justify-between bg-gray-900/80 rounded px-2 py-1 text-[10px] border border-gray-800 group/pen">
                                                        <span className="text-gray-400 truncate flex-1" title={pen.description}>{pen.description}</span>
                                                        <div className="flex items-center gap-2 shrink-0 ml-2">
                                                            {editingField === `pen-${pen.id}` ? (
                                                                <input type="number" className="w-14 bg-gray-700 text-white text-center text-xs rounded px-1 py-0.5" autoFocus
                                                                    value={editValue} onChange={e => setEditValue(e.target.value)}
                                                                    onBlur={() => handleEditPenalty(pen.id, Number(editValue))}
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleEditPenalty(pen.id, Number(editValue)); if (e.key === 'Escape') setEditingField(null); }}
                                                                />
                                                            ) : (
                                                                <span className={`font-mono font-bold cursor-pointer hover:text-f1-red transition-colors ${pen.points > 0 ? 'text-green-400' : 'text-red-400'}`}
                                                                    onClick={() => startEdit(`pen-${pen.id}`, pen.points)}>
                                                                    {pen.points > 0 ? '+' : ''}{pen.points}
                                                                </span>
                                                            )}
                                                            <button
                                                                onClick={() => handleDeletePenalty(pen.id, pen.description)}
                                                                className="text-gray-700 hover:text-red-500 transition-colors opacity-0 group-hover/pen:opacity-100"
                                                                title="Eintrag löschen"
                                                            >
                                                                <Trash2 size={11} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : view === 'TEAMS' && selectedTeam ? (
                        <>
                             <div className="p-4 bg-gray-900 border-b border-gray-700 flex justify-between items-center">
                                <div>
                                    <h3 className="text-lg font-bold text-white uppercase italic">{selectedTeam.name}</h3>
                                    <div className="text-xs text-gray-400">Punkte Historie (KWM)</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-mono font-bold text-white">{selectedTeam.total_points}</div>
                                    <div className="text-[10px] text-gray-500 uppercase font-bold">Total Points</div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                {teamDetails.map((row, idx) => (
                                    <div key={idx} className={`bg-gray-800/50 border border-gray-700 rounded p-3 ${row.is_test ? 'opacity-60 grayscale' : ''}`}>
                                        <div className="flex justify-between items-center mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold bg-gray-700 px-2 py-0.5 rounded text-gray-300">{row.round > 0 ? `R${row.round}` : 'TEST'}</span>
                                                <span className="font-bold text-white text-sm">{row.event_name}</span>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                 <div className={`font-mono font-bold text-lg ${row.is_test ? 'text-gray-500 line-through' : 'text-white'}`}>{row.total_points}</div>
                                                 {row.is_test && <span className="text-[9px] uppercase text-yellow-500 font-bold">Zählt nicht</span>}
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-400 mb-2 italic">Fahrer: {row.drivers}</div>
                                        
                                        <div className="grid grid-cols-3 gap-2 text-[10px] bg-gray-900/50 p-2 rounded border border-gray-800">
                                            <div className="flex flex-col items-center border-r border-gray-700">
                                                <span className="text-gray-500 uppercase font-bold text-[8px]">Driver Points</span>
                                                <span className="text-white">{row.components.raw_sum}</span>
                                            </div>
                                            <div className="flex flex-col items-center border-r border-gray-700">
                                                <span className="text-blue-500 uppercase font-bold text-[8px]">Clean Bonus Diff</span>
                                                <span className="text-gray-300">{row.components.clean_deduct}</span>
                                                <span className="text-[8px] text-gray-600">(If disabled)</span>
                                            </div>
                                            <div className="flex flex-col items-center">
                                                <span className="text-red-500 uppercase font-bold text-[8px]">Penalties</span>
                                                <span className="text-red-400">{row.components.penalties}</span>
                                                <span className="text-[8px] text-gray-600">(If enabled)</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <Calculator size={48} className="opacity-20 mb-4"/>
                            <p>Wähle einen Eintrag aus der Liste.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

type SeasonSignupStatus = 'MAIN' | 'RESERVE' | 'OUT';

interface SeasonSignupCampaign {
    id: number;
    season_name: string;
    discord_message?: string | null;
    deadline?: string | null;
    is_active: boolean;
    sent_at?: string | null;
}

interface SeasonSignupAdminData {
    campaign: SeasonSignupCampaign | null;
    counts: Record<SeasonSignupStatus | 'PENDING', number>;
    responses: Array<{
        user_id: string;
        username: string;
        response_status: SeasonSignupStatus;
        updated_at: string;
    }>;
}

const signupLabels: Record<SeasonSignupStatus, string> = {
    MAIN: 'Stammfahrer',
    RESERVE: 'Ersatzfahrer',
    OUT: 'Nicht dabei',
};

const SeasonSignupAdmin: React.FC<{ userId: string }> = ({ userId }) => {
    const [campaign, setCampaign] = useState<SeasonSignupCampaign | null>(null);
    const [seasonName, setSeasonName] = useState('');
    const [deadline, setDeadline] = useState('');
    const [discordMessage, setDiscordMessage] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [counts, setCounts] = useState<SeasonSignupAdminData['counts']>({ MAIN: 0, RESERVE: 0, OUT: 0, PENDING: 0 });
    const [responses, setResponses] = useState<SeasonSignupAdminData['responses']>([]);
    const [loadingSignup, setLoadingSignup] = useState(true);
    const [savingSignup, setSavingSignup] = useState(false);
    const [sendingSignup, setSendingSignup] = useState(false);

    const loadSignup = async () => {
        setLoadingSignup(true);
        try {
            const data = await apiRequest<SeasonSignupAdminData>(`season_signup.php?mode=admin&userId=${encodeURIComponent(userId)}`);
            setCampaign(data.campaign);
            setCounts(data.counts || { MAIN: 0, RESERVE: 0, OUT: 0, PENDING: 0 });
            setResponses(data.responses || []);
            if (data.campaign) {
                setSeasonName(data.campaign.season_name || '');
                setDeadline(data.campaign.deadline || '');
                setDiscordMessage(data.campaign.discord_message || '');
                setIsActive(Boolean(data.campaign.is_active));
            }
        } catch (error: any) {
            alert(error.message || 'Saison-Anmeldung konnte nicht geladen werden.');
        } finally {
            setLoadingSignup(false);
        }
    };

    useEffect(() => { loadSignup(); }, [userId]);

    const saveCampaign = async () => {
        if (!seasonName.trim()) {
            alert('Bitte zuerst die Saisonbezeichnung eintragen.');
            return null;
        }
        setSavingSignup(true);
        try {
            const result = await apiRequest<{ campaign: SeasonSignupCampaign; counts: SeasonSignupAdminData['counts'] }>('season_signup.php', 'POST', {
                action: 'save_campaign',
                adminUserId: userId,
                campaignId: campaign?.id,
                seasonName: seasonName.trim(),
                deadline: deadline || null,
                discordMessage: discordMessage.trim(),
                isActive,
            });
            setCampaign(result.campaign);
            setCounts(result.counts || counts);
            return result.campaign;
        } catch (error: any) {
            alert(error.message || 'Saison-Anmeldung konnte nicht gespeichert werden.');
            return null;
        } finally {
            setSavingSignup(false);
        }
    };

    const saveAndSend = async () => {
        if (!confirm(`Die Anmeldung für „${seasonName || 'die neue Saison'}“ jetzt speichern, aktivieren und an @everyone auf Discord senden?`)) return;
        setSendingSignup(true);
        try {
            const saved = await saveCampaign();
            if (!saved) return;
            const result = await apiRequest<{ campaign: SeasonSignupCampaign }>('season_signup.php', 'POST', {
                action: 'send_discord',
                adminUserId: userId,
                campaignId: saved.id,
            });
            setCampaign(result.campaign);
            setIsActive(true);
            await loadSignup();
            alert('Die Saison-Anmeldung ist aktiv und wurde auf Discord angekündigt.');
        } catch (error: any) {
            alert(error.message || 'Discord-Nachricht konnte nicht gesendet werden.');
        } finally {
            setSendingSignup(false);
        }
    };

    const responseStyle: Record<SeasonSignupStatus, string> = {
        MAIN: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
        RESERVE: 'border-blue-500/20 bg-blue-500/10 text-blue-300',
        OUT: 'border-gray-500/20 bg-gray-500/10 text-gray-300',
    };

    if (loadingSignup) {
        return <div className="grid min-h-[24rem] place-items-center rounded-[2rem] border border-white/[0.07] bg-[#11141b]"><Loader2 className="animate-spin text-f1-red" size={30}/></div>;
    }

    return (
        <div className="space-y-6">
            <section className="relative overflow-hidden rounded-[2rem] border border-white/[0.08] bg-[linear-gradient(125deg,rgba(239,18,8,.16),rgba(16,18,25,.98)_48%,rgba(10,11,16,.98))] p-6 shadow-2xl md:p-8">
                <div className="absolute -right-14 -top-16 h-56 w-56 rounded-full bg-f1-red/10 blur-3xl"/>
                <div className="relative grid gap-6 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                    <div>
                        <span className="inline-flex items-center gap-2 rounded-full border border-f1-red/25 bg-f1-red/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-red-100"><Radio size={13}/> Fahrerplanung</span>
                        <h2 className="sdc-display mt-4 text-4xl text-white md:text-5xl">Saison-Anmeldung</h2>
                        <p className="mt-3 max-w-2xl text-sm leading-6 text-white/55">Öffne die Zusage für die kommende Saison, sammle alle Antworten zentral und kündige sie über den vorhandenen Discord-Bot an.</p>
                    </div>
                    <div className={`inline-flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-xs font-black uppercase tracking-wider ${isActive ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-white/[0.04] text-gray-400'}`}>
                        <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,.8)]' : 'bg-gray-600'}`}/>
                        {isActive ? 'Dashboard-Abfrage aktiv' : 'Abfrage verborgen'}
                    </div>
                </div>
            </section>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="rounded-[1.75rem] border border-white/[0.075] bg-[#12151d] p-6 shadow-xl">
                    <div className="grid gap-5 md:grid-cols-2">
                        <label className="block">
                            <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.18em] text-white/40">Saisonbezeichnung</span>
                            <input value={seasonName} onChange={e => setSeasonName(e.target.value)} placeholder="z. B. Saison 12" className="w-full rounded-[1rem] border border-white/10 bg-black/25 px-4 py-3.5 text-sm font-bold text-white outline-none transition focus:border-f1-red/60"/>
                        </label>
                        <label className="block">
                            <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.18em] text-white/40">Antwortfrist (optional)</span>
                            <div className="relative">
                                <CalendarDays className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={17}/>
                                <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="w-full rounded-[1rem] border border-white/10 bg-black/25 py-3.5 pl-11 pr-4 text-sm font-bold text-white outline-none transition focus:border-f1-red/60"/>
                            </div>
                        </label>
                    </div>

                    <label className="mt-5 block">
                        <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.18em] text-white/40">Discord-Nachricht</span>
                        <textarea value={discordMessage} onChange={e => setDiscordMessage(e.target.value)} rows={6} placeholder="Die Anmeldung für unsere neue Saison ist geöffnet. Bitte gebt eure Rückmeldung im Dashboard ab." className="w-full resize-y rounded-[1.1rem] border border-white/10 bg-black/25 px-4 py-4 text-sm leading-6 text-white outline-none transition placeholder:text-white/20 focus:border-f1-red/60"/>
                        <span className="mt-2 block text-xs text-white/30">Saisonname, Auswahlmöglichkeiten und der Hinweis auf das Dashboard werden automatisch ergänzt.</span>
                    </label>

                    <button type="button" onClick={() => setIsActive(value => !value)} className="mt-5 flex w-full items-center justify-between rounded-[1.1rem] border border-white/[0.08] bg-white/[0.035] px-4 py-4 text-left transition hover:bg-white/[0.06]">
                        <div>
                            <div className="text-sm font-black text-white">Abfrage im Dashboard anzeigen</div>
                            <div className="mt-1 text-xs text-white/35">Bei Deaktivierung bleiben bisherige Antworten erhalten.</div>
                        </div>
                        <span className={`relative h-7 w-12 rounded-full transition ${isActive ? 'bg-f1-red' : 'bg-gray-700'}`}><span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${isActive ? 'left-6' : 'left-1'}`}/></span>
                    </button>

                    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                        <Button variant="secondary" onClick={saveCampaign} disabled={savingSignup || sendingSignup} className="flex-1 border border-white/10 bg-white/[0.05] font-black">
                            {savingSignup ? <Loader2 className="mr-2 animate-spin" size={17}/> : <Save className="mr-2" size={17}/>} Nur speichern
                        </Button>
                        <Button onClick={saveAndSend} disabled={savingSignup || sendingSignup} className="flex-[1.4] border-none bg-f1-red font-black text-white shadow-lg shadow-red-950/30 hover:brightness-110">
                            {sendingSignup ? <Loader2 className="mr-2 animate-spin" size={17}/> : <Send className="mr-2" size={17}/>} Speichern & Discord senden
                        </Button>
                    </div>
                    {campaign?.sent_at && <p className="mt-4 text-center text-xs text-white/30">Zuletzt an Discord gesendet: {new Date(campaign.sent_at).toLocaleString('de-DE')}</p>}
                </div>

                <aside className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { label: 'Stamm', value: counts.MAIN, icon: UserCheck, color: 'text-emerald-300 bg-emerald-500/10' },
                            { label: 'Ersatz', value: counts.RESERVE, icon: UserMinus, color: 'text-blue-300 bg-blue-500/10' },
                            { label: 'Nicht dabei', value: counts.OUT, icon: UserX, color: 'text-gray-300 bg-gray-500/10' },
                            { label: 'Offen', value: counts.PENDING, icon: Users, color: 'text-amber-300 bg-amber-500/10' },
                        ].map(stat => <div key={stat.label} className="rounded-[1.35rem] border border-white/[0.07] bg-[#12151d] p-4"><div className={`grid h-9 w-9 place-items-center rounded-xl ${stat.color}`}><stat.icon size={17}/></div><div className="mt-4 text-2xl font-black text-white">{stat.value}</div><div className="mt-1 text-[9px] font-black uppercase tracking-wider text-white/35">{stat.label}</div></div>)}
                    </div>
                    <div className="rounded-[1.5rem] border border-white/[0.07] bg-[#12151d] p-5">
                        <div className="mb-4 flex items-center justify-between"><h3 className="text-sm font-black text-white">Antworten</h3><span className="text-[10px] font-bold text-white/30">{responses.length} eingegangen</span></div>
                        <div className="max-h-[24rem] space-y-2 overflow-y-auto pr-1">
                            {responses.length === 0 ? <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-xs text-white/30">Noch keine Rückmeldungen</div> : responses.map(response => (
                                <div key={response.user_id} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-black/20 px-3 py-3">
                                    <span className="truncate text-sm font-bold text-white/80">{response.username}</span>
                                    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-wide ${responseStyle[response.response_status]}`}>{signupLabels[response.response_status]}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export const SystemConsole: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'CONSOLE' | 'AUDIT' | 'SEASON_SIGNUP'>('CONSOLE');
    const [logContent, setLogContent] = useState('');
    const [loading, setLoading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Only WebAdmins
    if (!user || !user.roles.includes(UserRole.WEBADMIN)) {
        return <Navigate to="/" />;
    }

    const fetchLog = async () => {
        setLoading(true);
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const res = await fetch(`${baseUrl}/admin_log.php?t=${Date.now()}`);
            if (res.status === 404) {
                setLogContent("Error 404: admin_log.php not found.");
                return;
            }
            const text = await res.text();
            setLogContent(text);
        } catch (e: any) {
            setLogContent(`Error fetching log: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const runCron = async () => {
        if(!confirm("Cronjob manuell starten? Dies sendet ggf. Erinnerungen an Discord.")) return;
        setLoading(true);
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const res = await fetch(`${baseUrl}/cron.php`);
            const text = await res.text();
            alert("Cron Output:\n" + text);
        } catch(e: any) { alert(e.message); }
        finally { setLoading(false); }
    }

    const clearLog = async () => {
        if(!confirm("Log wirklich leeren?")) return;
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            await fetch(`${baseUrl}/admin_log.php`, { method: 'DELETE' });
            setLogContent("--- Log cleared ---");
        } catch(e: any) {
            alert(e.message);
        }
    };

    useEffect(() => {
        if (activeTab === 'CONSOLE') fetchLog();
    }, [activeTab]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
    }, [logContent]);

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase italic flex items-center gap-3 text-f1-red">
                        <Terminal size={32}/> System Console
                    </h1>
                    <p className="text-gray-400 text-sm">Server-side Debug Logs & Point Audit</p>
                </div>
                
                {/* TABS */}
                <div className="flex max-w-full gap-1 overflow-x-auto rounded bg-gray-800 p-1">
                    <button 
                        onClick={() => setActiveTab('CONSOLE')}
                        className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab === 'CONSOLE' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Terminal size={14}/> Console
                    </button>
                    <button 
                        onClick={() => setActiveTab('AUDIT')}
                        className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab === 'AUDIT' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Calculator size={14}/> Punkte Archiv
                    </button>
                    <button
                        onClick={() => setActiveTab('SEASON_SIGNUP')}
                        className={`px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-2 transition-colors ${activeTab === 'SEASON_SIGNUP' ? 'bg-f1-red text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        <Megaphone size={14}/> Saison-Anmeldung
                    </button>
                </div>
            </div>

            {activeTab === 'CONSOLE' ? (
                <>
                    <div className="flex justify-end gap-2 mb-2">
                        <Button variant="secondary" size="sm" icon={Clock} onClick={runCron} disabled={loading}>
                            Trigger Cron
                        </Button>
                        <Button variant="danger" size="sm" icon={Trash2} onClick={clearLog}>Clear Log</Button>
                        <Button variant="secondary" size="sm" icon={RefreshCw} onClick={fetchLog} disabled={loading}>
                            {loading ? 'Lade...' : 'Refresh'}
                        </Button>
                    </div>

                    <Card className="bg-[#0f0f13] border-gray-700 p-0 overflow-hidden shadow-2xl">
                        <div className="p-2 bg-gray-800 border-b border-gray-700 flex gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-xs text-gray-400 ml-2 font-mono">user@sdc-server:~/uploads/debug_log.txt</span>
                        </div>
                        <textarea 
                            ref={textareaRef}
                            readOnly
                            className="w-full h-[600px] bg-[#0f0f13] text-green-400 font-mono text-xs p-4 focus:outline-none resize-none"
                            value={logContent}
                        />
                    </Card>

                    <div className="p-4 bg-blue-900/20 border border-blue-800 rounded flex gap-3">
                        <Shield className="text-blue-400 shrink-0"/>
                        <div className="text-sm text-blue-200">
                            <strong>Tipp:</strong> Der "Cron"-Button simuliert den regelmäßigen Server-Aufruf. Benutze ihn, um teure Berechnungen oder Erinnerungen zu testen.
                        </div>
                    </div>
                </>
            ) : activeTab === 'AUDIT' ? (
                <PointsAudit />
            ) : (
                <SeasonSignupAdmin userId={user.id}/>
            )}
        </div>
    );
};
